import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  GridLegacy as Grid,
  Card,
  CardContent,
  Button,
  Tabs,
  Tab,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Analytics as AnalyticsIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CloudUpload as ImportIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import TradeForm from '../components/trading-journal/TradeForm';
import TradesList from '../components/trading-journal/TradesList';
import TradingStatistics from '../components/trading-journal/TradingStatistics';
import TradingAnalytics from '../components/trading-journal/TradingAnalytics';
import TradeImport from '../components/trading-journal/TradeImport';
import { useTradingJournal } from '../contexts/TradingJournalContext';
import FeatureGuard from '../components/FeatureGuard';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`trading-journal-tabpanel-${index}`}
      aria-labelledby={`trading-journal-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const TradingJournalPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [showTradeForm, setShowTradeForm] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const {
    trades,
    loading,
    addTrade,
    updateTrade,
    deleteTrade,
    deleteTrades,
    getTradeStatistics,
  } = useTradingJournal();

  const statistics = getTradeStatistics();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleAddTrade = () => {
    setShowTradeForm(true);
  };

  const handleCloseTradeForm = () => {
    setShowTradeForm(false);
  };

  const handleOpenImport = () => {
    setShowImportDialog(true);
  };

  const handleCloseImport = () => {
    setShowImportDialog(false);
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.4 },
    },
  };

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
    <Container maxWidth="xl">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          flexDirection={isMobile ? 'column' : 'row'}
          gap={2}
        >
          <Box>
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
              Trading Journal
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Track, analyze, and improve your trading performance
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
            <Button
              variant="outlined"
              startIcon={<ImportIcon />}
              onClick={handleOpenImport}
              size="large"
              sx={{
                minWidth: 160,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Import Trades
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddTrade}
              size="large"
              sx={{
                minWidth: 160,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Add New Trade
            </Button>
          </Box>
        </Box>

        {/* Quick Stats Cards */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div variants={cardVariants}>
              <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h4" fontWeight="bold" color="primary">
                        {trades.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Trades
                      </Typography>
                    </Box>
                    <TimelineIcon color="primary" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <motion.div variants={cardVariants}>
              <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h4" fontWeight="bold" color="success.main">
                        {statistics.winRate.toFixed(1)}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Win Rate
                      </Typography>
                    </Box>
                    <TrendingUpIcon color="success" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <motion.div variants={cardVariants}>
              <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography 
                        variant="h4" 
                        fontWeight="bold" 
                        color={statistics.totalPnL >= 0 ? 'success.main' : 'error.main'}
                      >
                        ${statistics.totalPnL.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total P&L
                      </Typography>
                    </Box>
                    {statistics.totalPnL >= 0 ? (
                      <TrendingUpIcon color="success" sx={{ fontSize: 40 }} />
                    ) : (
                      <TrendingDownIcon color="error" sx={{ fontSize: 40 }} />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <motion.div variants={cardVariants}>
              <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h4" fontWeight="bold" color="info.main">
                        {statistics.averageRR.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Avg Risk/Reward
                      </Typography>
                    </Box>
                    <AssessmentIcon color="info" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>

        {/* Main Content Tabs */}
        <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant={isMobile ? 'scrollable' : 'standard'}
              scrollButtons="auto"
              sx={{ px: 2 }}
            >
              <Tab
                label="All Trades"
                icon={<TimelineIcon />}
                iconPosition="start"
                sx={{ textTransform: 'none', fontWeight: 600 }}
              />
              <Tab
                label="Statistics"
                icon={<AnalyticsIcon />}
                iconPosition="start"
                sx={{ textTransform: 'none', fontWeight: 600 }}
              />
              <Tab
                label="Analytics"
                icon={<AssessmentIcon />}
                iconPosition="start"
                sx={{ textTransform: 'none', fontWeight: 600 }}
              />
              <Tab
                label="Performance"
                icon={<TrendingUpIcon />}
                iconPosition="start"
                sx={{ textTransform: 'none', fontWeight: 600 }}
              />
            </Tabs>
          </Box>

          <TabPanel value={activeTab} index={0}>
            <TradesList
              trades={trades}
              onUpdateTrade={updateTrade}
              onDeleteTrade={deleteTrade}
              onDeleteTrades={deleteTrades}
              loading={loading}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <TradingStatistics trades={trades} />
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <TradingAnalytics trades={trades} />
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            <TradingAnalytics trades={trades} />
          </TabPanel>
        </Card>

        {/* Trade Form Dialog */}
        <TradeForm
          open={showTradeForm}
          onClose={handleCloseTradeForm}
          onSubmit={addTrade}
        />

        {/* Trade Import Dialog */}
        <TradeImport
          open={showImportDialog}
          onClose={handleCloseImport}
        />
      </motion.div>
    </Container>
    </Box>
  );
};

export default function TradingJournal() {
  return (
    <FeatureGuard featureId="trading_journal">
      <TradingJournalPage />
    </FeatureGuard>
  );
} 